import type { RouteLocationNamedRaw } from 'vue-router'

import type { Contact } from '@/shared/types/api'
import type { RoomListItem } from '@/shared/types/app'
import type { Room } from '@/shared/types/store'
import { pathNames } from '@/shared/utils/constants'

export type ChatRouteSource = Contact | Room | RoomListItem | string

/**
 * Извлекает идентификатор чата из сущности и возвращает значение для params.id маршрута (base64).
 * Contact → imLogin, Room/RoomListItem → roomId ?? id ?? pServedUser, string → как есть.
 */
export const getChatRouteId = (source: ChatRouteSource): string | null => {
  if (typeof source === 'string') {
    return source ? btoa(source) : null
  }

  if ('imLogin' in source || 'internalNumber' in source) {
    const contact = source as Contact
    const login = contact.imLogin || contact.internalNumber
    return login ? btoa(login) : null
  }

  const room = source as RoomListItem | Room
  const rawId = 'roomId' in room && room.roomId
    ? room.roomId
    : room.id ?? room.pServedUser
  return rawId ? btoa(rawId) : null
}

export type ChatRouteOptions = {
  query?: Record<string, string>
  hash?: string
}

/**
 * Возвращает объект маршрута для перехода в чат (router.push / router.resolve).
 * Если идентификатор извлечь нельзя, возвращает null.
 * Блокировка без InstantMessaging/веб-клиента — в optionalPush / beforeEnter.
 */
export const getChatRoute = (
  source: ChatRouteSource,
  options?: ChatRouteOptions,
): RouteLocationNamedRaw | undefined => {
  const id = getChatRouteId(source)
  if (id == null) {
    return undefined
  }
  const route: RouteLocationNamedRaw = {
    name: pathNames.CHAT,
    params: { id },
  }
  if (options?.query && Object.keys(options.query).length > 0) {
    route.query = options.query
  }
  if (options?.hash) {
    route.hash = `#${options.hash}`
  }

  return route
}

/**
 * Возвращает путь к чату для href (например /chat/xxx). Для router.push используйте getChatRoute.
 */
export const getChatPath = (source: ChatRouteSource): string | null => {
  const id = getChatRouteId(source)
  return id == null ? null : `/chat/${id}`
}
